import { PREFIXO_HASH_DELEGADO } from '../delegacao/atuacao';
import type { QueryKey } from '@tanstack/react-query';

import { formatQueryKey, recordPersistDecision } from './offlineDebug';
import { queryKeys } from './queryConfig';

const PERSISTED_QUERY_PREFIXES: ReadonlyArray<readonly unknown[]> = [
  queryKeys.processos.all,
  queryKeys.comunicacoes.all,
  // KPIs da home (barra de comunicações/processos + card de sync DJEN) vêm
  // todos de /dashboard/stats. Sem persistir, esses dois widgets ficavam no
  // spinner aguardando rede fresca enquanto o resto da tela já vinha do cache.
  // Persistir dá render otimista e revalidação em background, igual aos demais.
  queryKeys.dashboard.stats,
  // A régua do painel (topo da home): offline, mostra a última, com a data dela.
  queryKeys.dashboard.regua,
  queryKeys.documentos.all,
  queryKeys.documentos.artefatos,
  queryKeys.calendario.eventos,
  queryKeys.calendario.agenda,
  queryKeys.modelos.all,
  // Papel timbrado: config pequena e estável; persistir evita o spinner no card
  // de Configurações a cada cold start do PWA e mantém o preview offline.
  queryKeys.timbrado.me,
  queryKeys.processual.all,
  // Anotações do usuário nos autos: lidas offline junto com os autos que anotam.
  queryKeys.anotacoes.all,
  // Monitoramento da integração: persistido para que o histórico de uptime
  // e o status do snapshot sobrevivam a um restart da página enquanto o
  // autenticador estiver fora — sem isso, o usuário perde a visão histórica
  // exatamente quando precisa investigar o porquê da queda.
  ['admin', 'monitoramento'] as const,
];

const loggedPersistDecisions = new Map<string, string>();

export type QueryPersistPolicy = 'always' | 'never' | 'nonEmpty' | 'conditional';

export const queryPersistMeta = {
  always: { persist: 'always' as const },
  never: { persist: 'never' as const },
  nonEmpty: { persist: 'nonEmpty' as const },
  when<T = unknown>(predicate: (data: T) => boolean) {
    return {
      persist: 'conditional' as const,
      persistWhen: predicate as (data: unknown) => boolean,
    };
  },
};

type PersistMeta = {
  persist?: QueryPersistPolicy;
  persistWhen?: (data: unknown) => boolean;
};

type PersistableQuery = {
  meta?: PersistMeta;
  queryHash?: string;
  queryKey: QueryKey;
  state: { status: string; data: unknown };
};

export interface PersistDecision {
  persisted: boolean;
  policy: QueryPersistPolicy;
  reason: string;
}

function queryKeyStartsWith(queryKey: QueryKey, prefix: readonly unknown[]): boolean {
  if (queryKey.length < prefix.length) return false;

  return prefix.every((part, index) => queryKey[index] === part);
}

export function isPersistableQueryKey(queryKey: QueryKey): boolean {
  return PERSISTED_QUERY_PREFIXES.some((prefix) =>
    queryKeyStartsWith(queryKey, prefix),
  );
}

export function isPersistableQueryData(data: unknown): boolean {
  if (data == null) return false;

  if (Array.isArray(data)) {
    return data.length > 0;
  }

  if (typeof data === 'object') {
    const record = data as Record<string, unknown>;

    if ('items' in record && Array.isArray(record.items)) {
      return record.items.length > 0;
    }
  }

  return true;
}

export function getPersistPolicy(query: PersistableQuery): QueryPersistPolicy {
  const metaPolicy = query.meta?.persist;

  if (
    metaPolicy === 'always'
    || metaPolicy === 'never'
    || metaPolicy === 'nonEmpty'
    || metaPolicy === 'conditional'
  ) {
    return metaPolicy;
  }

  return isPersistableQueryKey(query.queryKey) ? 'nonEmpty' : 'never';
}

export function getPersistDecision(query: PersistableQuery): PersistDecision {
  const policy = getPersistPolicy(query);

  // Contexto delegado não persiste nada (P11 — offline delegado é pendência): o dado
  // é da conta do titular e não pode ficar no disco do delegado.
  if (query.queryHash?.startsWith(PREFIXO_HASH_DELEGADO)) {
    return { persisted: false, policy, reason: 'contexto-delegado' };
  }

  if (query.state.status !== 'success') {
    return { persisted: false, policy, reason: 'status-not-success' };
  }

  if (policy === 'never') {
    return { persisted: false, policy, reason: 'policy-never' };
  }

  if (policy === 'always') {
    return { persisted: true, policy, reason: 'policy-always' };
  }

  if (policy === 'conditional') {
    const predicate = query.meta?.persistWhen;
    if (!predicate) {
      return { persisted: false, policy, reason: 'conditional-no-predicate' };
    }
    return predicate(query.state.data)
      ? { persisted: true, policy, reason: 'conditional-matched' }
      : { persisted: false, policy, reason: 'conditional-unmatched' };
  }

  return isPersistableQueryData(query.state.data)
    ? { persisted: true, policy, reason: 'policy-nonEmpty' }
    : { persisted: false, policy, reason: 'empty-data' };
}

function recordPersistDecisionIfNeeded(query: PersistableQuery, decision: PersistDecision) {
  if (!import.meta.env.DEV) return;

  const key = query.queryHash ?? formatQueryKey(query.queryKey);
  const signature = `${decision.policy}:${decision.persisted}:${decision.reason}`;

  if (loggedPersistDecisions.get(key) === signature) return;

  loggedPersistDecisions.set(key, signature);
  recordPersistDecision({
    persisted: decision.persisted,
    policy: decision.policy,
    queryHash: query.queryHash ?? null,
    queryKey: formatQueryKey(query.queryKey),
    reason: decision.reason,
  });
}

export function shouldPersistQuery(query: PersistableQuery): boolean {
  const decision = getPersistDecision(query);
  recordPersistDecisionIfNeeded(query, decision);
  return decision.persisted;
}
